/**
 * MSW handlers for the Payroll Documents demo.
 *
 * The real requests get answered: the per-owner list, the signature queue, the company-wide
 * library, and the document's content, which the backend streams through from the provider.
 * Modelled on `Overview/demo/demoHandlers.ts` and `RunPayroll/demo/demoHandlers.ts` (the PDF
 * fixture).
 */

// msw is a devDependency but this file is only used by Storybook, which includes devDependencies in its build
// eslint-disable-next-line import/no-extraneous-dependencies
import { http, HttpResponse, delay } from 'msw';

import { MOCK_DOCUMENTS, MOCK_INCOMPLETE_LIBRARY, MOCK_LIBRARY, MOCK_SIGNATURE_QUEUE } from '../documents.mocks';

import providerUnavailableResponse from 'modules/Payroll/common/demo/providerUnavailable';
import { demoPaystubPdf } from 'services/payroll/mutations/useEmployeePaystub/useEmployeePaystub.mocks';

const DOCUMENTS_URL = '*/api/payroll/v1/documents/';
const SIGNATURE_QUEUE_URL = '*/api/payroll/v1/documents/signature-queue/';
const LIBRARY_URL = '*/api/payroll/v1/documents/library/';
const CONTENT_URL_PATTERN = '*/api/payroll/v1/documents/:documentUuid/content/';

/**
 * Filters by `worker_uuid` when the caller sends one — the Team detail takeover's Documents tab
 * always does — and paginates the (filtered) fixture the way the server does, so the pagination
 * control is exercised for callers that don't filter.
 */
const documentsHandler = http.get(DOCUMENTS_URL, ({ request }) => {
    const url = new URL(request.url);
    const workerUuid = url.searchParams.get('worker_uuid');
    const documents = workerUuid
        ? MOCK_DOCUMENTS.filter((document) => document.worker_uuid === workerUuid)
        : MOCK_DOCUMENTS;
    const limit = Number(url.searchParams.get('limit') ?? documents.length);
    const offset = Number(url.searchParams.get('offset') ?? 0);

    return HttpResponse.json({
        count: documents.length,
        results: documents.slice(offset, offset + limit),
    });
});

/** The documents list failing outright. */
export const failingDocumentsHandler = http.get(DOCUMENTS_URL, () => providerUnavailableResponse());

/** Never resolves, so the list's loading skeleton stays put for visual inspection. */
export const pendingDocumentsHandler = http.get(DOCUMENTS_URL, async () => {
    await delay('infinite');
});

const signatureQueueHandler = http.get(SIGNATURE_QUEUE_URL, () => HttpResponse.json(MOCK_SIGNATURE_QUEUE));

/** Nothing left to sign: the page drops the "Needs your signature" section and the "All documents" heading. */
export const emptySignatureQueueHandler = http.get(SIGNATURE_QUEUE_URL, () =>
    HttpResponse.json({ count: 0, results: [] })
);

/** The queue failing on its own. The library still renders, with the queue's rows kept in it. */
export const failingSignatureQueueHandler = http.get(SIGNATURE_QUEUE_URL, () => providerUnavailableResponse());

/** Never resolves, so the library shows its `needs_your_signature` rows until the queue arrives. */
export const pendingSignatureQueueHandler = http.get(SIGNATURE_QUEUE_URL, async () => {
    await delay('infinite');
});

const libraryHandler = http.get(LIBRARY_URL, () => HttpResponse.json(MOCK_LIBRARY));

/** Some owners' reads failed or were skipped: a warning names them over the rows that did load. */
export const incompleteLibraryHandler = http.get(LIBRARY_URL, () => HttpResponse.json(MOCK_INCOMPLETE_LIBRARY));

/** The provider unreachable for everyone. The signature queue stays usable. */
export const failingLibraryHandler = http.get(LIBRARY_URL, () => providerUnavailableResponse());

/** Never resolves, so the library's skeleton rows stay put — what a big roster looks like while it reads. */
export const pendingLibraryHandler = http.get(LIBRARY_URL, async () => {
    await delay('infinite');
});

const contentHandler = http.get(CONTENT_URL_PATTERN, () =>
    HttpResponse.arrayBuffer(demoPaystubPdf().buffer as ArrayBuffer, { headers: { 'Content-Type': 'application/pdf' } })
);

/** The backend unable to read the document from the provider. */
export const failingContentHandler = http.get(CONTENT_URL_PATTERN, () =>
    HttpResponse.json({ error: { code: 'DOCUMENT_UNAVAILABLE' } }, { status: 502 })
);

/** Never delivers the file — the row, or the viewer, stays on progress indefinitely. */
export const pendingContentHandler = http.get(CONTENT_URL_PATTERN, async () => {
    await delay('infinite');
});

/**
 * Keyed by endpoint so a story overrides one by name — `{ ...allDemoHandlers, content: ... }` —
 * rather than relying on array order.
 */
const allDemoHandlers = {
    documents: documentsHandler,
    signatureQueue: signatureQueueHandler,
    library: libraryHandler,
    content: contentHandler,
};

export default allDemoHandlers;
